import { useEffect, useRef, useState } from 'react';
import { useAdobeImsAuth } from './auth/useAdobeImsAuth';
import { Header } from './components/Header';
import { TabNav } from './components/TabNav';
import { FilterBar } from './components/FilterBar';
import { VPPanel } from './components/panels/VPPanel';
import { SDPanel } from './components/panels/SDPanel';
import { ODPanel } from './components/panels/ODPanel';
import { AvailabilityTrackerPanel } from './components/panels/AvailabilityTrackerPanel';
import { HeatmapModal } from './components/HeatmapModal';
import { UploadCsvModal } from './components/UploadCsvModal';
import { useAppState } from './hooks/useAppState';
import { DealsProvider, useDeals } from './context/DealsContext';
import { OrgProvider } from './context/OrgContext';
import { fetchDeals, triggerSync } from './utils/database';
import { friendlyTime } from './utils/friendlyTime';
import { getLatestFY } from './utils/filter';
import type { TabState } from './types';
function Spinner({
  size = 16,
  color = '#6B7280'
}: {
  size?: number;
  color?: string;
}) {
  return <svg width={size} height={size} viewBox="0 0 24 24" className="animate-spin" style={{
    color
  }} data-id="element-0" data-mp="3mljat0fdtn5p">
      <circle cx="12" cy="12" r="10" fill="none" stroke="currentColor" strokeWidth="3" strokeOpacity="0.25" data-id="element-1" data-mp="3rc9d24sih9w6" />
      <path d="M12 2a10 10 0 0 1 10 10" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" data-id="element-2" data-mp="5orh0l86u6j" />
    </svg>;
}
const ADOBE_LOGO_URL = 'https://cdn.magicpatterns.com/uploads/6yi4FNzoPfGEun9849jQCu/Screenshot_2026-02-03_at_7.33.48_PM.png';

// Inner component has access to DealsProvider context
function Dashboard() {
  const {
    accessLevel,
    error: authError,
    isLoading: isAuthLoading,
    isReady: isAuthReady,
    isSignedIn,
    signIn,
    signOut,
    user
  } = useAdobeImsAuth();
  const {
    state,
    currentTab,
    updateTab,
    switchTab,
    drillRegion
  } = useAppState();
  const {
    deals,
    setDeals,
    setSyncMeta,
    syncStatus,
    lastSynced,
    isLiveData
  } = useDeals();

  // Default the FY filter to the latest fiscal year present in the data, once, the first
  // time real data loads — across all three tabs. Doesn't override a later manual choice.
  const hasAutoSelectedFY = useRef(false);
  useEffect(() => {
    if (hasAutoSelectedFY.current || deals.length === 0) return;
    const latestFY = getLatestFY(deals);
    if (!latestFY) return;
    hasAutoSelectedFY.current = true;
    updateTab('vp', {
      fy: latestFY
    });
    updateTab('sd', {
      fy: latestFY
    });
    updateTab('od', {
      fy: latestFY
    });
  }, [deals, updateTab]);
  const [syncError, setSyncErrorMsg] = useState<string | null>(null);
  const [showHeatmap, setShowHeatmap] = useState(false);
  const [showUploadCsv, setShowUploadCsv] = useState(false);
  const canSync = accessLevel === 'editor' || accessLevel === 'admin';
  const refreshDeals = () => {
    setSyncMeta('loading');
    return fetchDeals().then(data => {
      if (data && data.records.length > 0) {
        setDeals(data.records);
        setSyncMeta(data.stale ? 'error' : 'success', data.lastSyncedAt ?? undefined);
      } else {
        setSyncMeta('never');
      }
    }).catch(err => {
      console.error('fetchDeals failed:', err);
      setSyncMeta('error');
    });
  };

  // Fetch live deal data once the user is authenticated and provisioned
  useEffect(() => {
    if (!isSignedIn || accessLevel === 'unregistered' || accessLevel === 'anonymous') return;
    void refreshDeals();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSignedIn, accessLevel]);
  const handleManualSync = async () => {
    setSyncMeta('loading');
    setSyncErrorMsg(null);
    try {
      const result = await triggerSync();
      if (result) {
        if (result.records.length > 0) {
          setDeals(result.records);
        }
        setSyncMeta(result.stale ? 'error' : 'success', result.lastSyncedAt);
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Sync failed';
      setSyncErrorMsg(msg);
      setSyncMeta('error');
    }
  };
  if (!isAuthReady || isAuthLoading) {
    return <div className="flex h-screen items-center justify-center bg-slate-50 text-slate-500 font-sans" data-id="element-3" data-mp="m5dws7tulitu">
        Initializing Adobe sign-in...
      </div>;
  }
  if (!isSignedIn) {
    return <div className="flex min-h-screen items-center justify-center bg-slate-50 px-6 py-10 text-slate-900 font-sans" data-id="element-4" data-mp="c9apyoo1ww0k">
        <div className="w-full max-w-xl rounded-2xl border border-slate-200 bg-white p-8 shadow-sm" data-id="element-5" data-mp="19vhtrwcj6kbv">
          <div className="flex items-center gap-4" data-id="element-6" data-mp="14xpzivoeym4i">
            <img src={ADOBE_LOGO_URL} alt="Adobe OnDemand" className="h-14 w-auto" data-id="element-7" data-mp="zxamk581ngn2" />
            <div data-id="element-8" data-mp="vwie3e6zxfru">
              <h1 className="text-2xl font-bold text-slate-900" data-id="element-9" data-mp="vx06u0ug9bv0">OnDemand Team Dashboard</h1>
              <p className="mt-1 text-sm text-slate-500" data-id="element-10" data-mp="3kokm7g59f70f">Sign in with Adobe ID</p>
            </div>
          </div>

          <div className="mt-8 space-y-4" data-id="element-11" data-mp="3glehicxspnw2">
            <div className="rounded-xl border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-800" data-id="element-12" data-mp="3gok9bzxt73vh">
              Dashboard data is available only after Adobe IMS login.
              <br data-id="element-13" data-mp="206js98wzjk3d" /><br data-id="element-14" data-mp="206a29lbxxcxo" />
              <p data-id="element-15" data-mp="203nvvp0sd3h1">
                1. Click Sign-In<br data-id="element-16" data-mp="254lbpuybplfd" />
                2. Login using your company account<br data-id="element-17" data-mp="2530j4avkkck5" />
                3. Choose profile: Adobe Inc
              </p>
            </div>

            {authError && <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700" data-id="element-18" data-mp="2ev6gy18i5o79">
                {authError}
              </div>}

            <button onClick={() => void signIn()} disabled={isAuthLoading} className="w-full flex items-center justify-center gap-2 rounded-xl bg-red-600 px-6 py-3 text-sm font-semibold text-white hover:bg-red-700 disabled:opacity-60 transition-colors" data-id="element-19" data-mp="2jw3ziexfck9a">
              {isAuthLoading ? 'Signing in…' : 'Sign in with Adobe ID'}
            </button>
          </div>
        </div>
      </div>;
  }
  if (accessLevel === 'unregistered') {
    return <div className="flex min-h-screen items-center justify-center bg-slate-50 px-6 py-10 text-slate-900 font-sans" data-id="element-20" data-mp="21tf153nrvydq">
        <div className="w-full max-w-xl rounded-2xl border border-slate-200 bg-white p-8 shadow-sm" data-id="element-21" data-mp="2as0iwwlf79dg">
          <div className="flex items-center gap-4" data-id="element-22" data-mp="25tfesm31s5jq">
            <img src={ADOBE_LOGO_URL} alt="Adobe OnDemand" className="h-14 w-auto" data-id="element-23" data-mp="25rukmxknaome" />
            <div data-id="element-24" data-mp="2kp54au7fz0t7">
              <h1 className="text-2xl font-bold text-slate-900" data-id="element-25" data-mp="2kolpvnh02ujx">OnDemand Team Dashboard</h1>
              <p className="mt-1 text-sm text-slate-500" data-id="element-26" data-mp="2fr3bhhk7rn4j">Account not provisioned</p>
            </div>
          </div>

          <div className="mt-8 space-y-4" data-id="element-27" data-mp="2tkupp38w3v6l">
            <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900" data-id="element-28" data-mp="2pgwlie4pju6o">
              You are signed in with Adobe ID, but this account is not registered for dashboard
              access. No data is shown until an administrator assigns you a role in the system.
            </div>

            {authError && <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700" data-id="element-29" data-mp="3i1jvvbe2nlnt">
                {authError}
              </div>}

            <button onClick={() => void signOut()} disabled={isAuthLoading} className="w-full flex items-center justify-center gap-2 rounded-xl border border-slate-300 bg-white px-6 py-3 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-60 transition-colors" data-id="element-30" data-mp="384nnd5o6azj9">
              {isAuthLoading ? 'Signing out…' : 'Sign out'}
            </button>
          </div>
        </div>
      </div>;
  }
  return <OrgProvider isAdmin={accessLevel === 'admin' || accessLevel === 'editor'} data-id="element-31" data-mp="2ud93kwmfyub5">
    <div className="min-h-screen" data-id="element-32" data-mp="2gej5perssqlq">
      <Header user={user} accessLevel={accessLevel} isAuthLoading={isAuthLoading} onSignOut={() => void signOut()} data-id="element-33" data-mp="2gfkh5kcq7f3z" />

      {/* Sync status bar — visible to all; sync button only for editor/admin */}
      <div className="flex items-center gap-3 px-4 py-1.5 bg-slate-50 border-b border-slate-200 text-xs text-slate-500" data-id="element-34" data-mp="30u2h2bxtdvej">
        <span className="flex items-center gap-1.5" data-id="element-35" data-mp="2vx28nudhsgk9">
          {syncStatus === 'loading' && <><Spinner size={12} data-id="element-36" data-mp="2qvmoe4j52e2x" /><span data-id="element-37" data-mp="2qzaj370bl6j8">Loading live data…</span></>}
          {syncStatus === 'success' && `✓ Data synced${lastSynced ? ` · ${friendlyTime(lastSynced)}` : ''}`}
          {syncStatus === 'error' && `⚠ Showing deals cached ${lastSynced ? ` · ${friendlyTime(lastSynced)}` : ''}`}
          {syncStatus === 'never' && '— No live data yet; showing sample data'}
          {syncStatus === 'idle' && ''}
        </span>
        {syncError && <span className="text-red-600 ml-2" data-id="element-38" data-mp="237mzla5e0xqz">{syncError}</span>}
        <div className="ml-auto flex items-center gap-2" data-id="element-39" data-mp="2396i77einkvx">
          {isLiveData && accessLevel === 'admin' && <button onClick={() => setShowHeatmap(true)} className="px-3 py-1 rounded-md bg-indigo-600 text-white text-xs font-medium hover:bg-indigo-700 transition-colors" data-id="element-40" data-mp="1yamscvwsxoya">
              Coverage Heatmap
            </button>}
          {isLiveData && (accessLevel === 'admin' || accessLevel === 'editor') && <button onClick={() => setShowUploadCsv(true)} className="px-3 py-1 rounded-md bg-indigo-600 text-white text-xs font-medium hover:bg-indigo-700 transition-colors" data-id="element-41" data-mp="iqm7hmsbvfmf">
              Upload CSV
            </button>}
          {canSync && <button onClick={() => void handleManualSync()} disabled={syncStatus === 'loading'} className="px-3 py-1 rounded-md bg-slate-700 text-white text-xs font-medium hover:bg-slate-800 disabled:opacity-50 transition-colors flex items-center gap-1.5" data-id="element-42" data-mp="3r6fk3qjjg6y7">
              {syncStatus === 'loading' ? <><Spinner size={12} color="#fff" data-id="element-43" data-mp="38ewi9n192yxr" />Syncing…</> : 'Sync live data'}
            </button>}
        </div>
      </div>

      {showHeatmap && <HeatmapModal deals={deals} onClose={() => setShowHeatmap(false)} data-id="element-44" data-mp="2f97vd81ebcd2" />}

      {showUploadCsv && <UploadCsvModal onClose={() => setShowUploadCsv(false)} onUploaded={() => void refreshDeals()} data-id="element-45" data-mp="2k818gzcrt1cp" />}

      <TabNav currentTab={currentTab} onSwitch={switchTab} data-id="element-46" data-mp="2u44cybv3hc2t" />

      <div className="relative" data-id="element-47" data-mp="2u3mgpf8fmxuw">
        {/* Initial page-load overlay — shown while data is being fetched for the first time */}
        {syncStatus === 'loading' && !isLiveData && <div className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-3 bg-white/80 min-h-64" data-id="element-48" data-mp="21g8g975izk61">
            <Spinner size={36} color="#4F46E5" data-id="element-49" data-mp="26cql070uviid" />
            <p className="text-sm text-slate-500" data-id="element-50" data-mp="2b5s0ag4z5k2h">Loading dashboard data…</p>
          </div>}

        {currentTab === 'vp' && <>
            <FilterBar tab="vp" state={state.vp} onUpdate={u => updateTab('vp', u as Partial<TabState>)} data-id="element-51" data-mp="1do1o6k6u1zcf" />
            <VPPanel state={state.vp} onDrillRegion={drillRegion} data-id="element-52" data-mp="1imw8jqkg7vyn" />
          </>}
        {currentTab === 'sd' && <>
            <FilterBar tab="sd" state={state.sd} onUpdate={u => updateTab('sd', u as Partial<TabState>)} data-id="element-53" data-mp="3rxneqpa8r86x" />
            <SDPanel state={state.sd} data-id="element-54" data-mp="3mxrff3cho4xp" />
          </>}
        {currentTab === 'od' && <>
            <FilterBar tab="od" state={state.od} onUpdate={u => updateTab('od', u as Partial<TabState>)} data-id="element-55" data-mp="3hyy0r7lgu7vy" />
            <ODPanel state={state.od} data-id="element-56" data-mp="3d27hycia0y6u" />
          </>}
        {currentTab === 'at' && <AvailabilityTrackerPanel data-id="element-57" data-mp="3894n811rjip2" />}
      </div>
    </div>
    </OrgProvider>;
}
export default function App() {
  return <DealsProvider data-id="element-58" data-mp="3468aus2tgitk">
      <Dashboard data-id="element-59" data-mp="2z985ykhpdm41" />
    </DealsProvider>;
}